import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'

const PopupTooltip = forwardRef(({ text, open = false, storageKey = null, autoMarkSeenAfter = null, autoClose = false, onInteraction = null, top = "150%", bottom = null, right = null, left = null, width = "288px" }, ref) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const timerRef = useRef(null);
    const visibilityTimerRef = useRef(null);

    useImperativeHandle(ref, () => ({
        handleInteraction: () => {
            if (storageKey) {
                // Clear timer and immediately hide popup
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
                if (visibilityTimerRef.current) {
                    clearTimeout(visibilityTimerRef.current);
                }
                localStorage.setItem(`kodeo.${storageKey}`, 'true');
                setInternalOpen(false);
                setIsVisible(false);
            }
        }
    }));

    useEffect(() => {
        if (open && storageKey) {
            // Check if user has already seen this popup
            const hasSeenPopup = localStorage.getItem(`kodeo.${storageKey}`) === 'true';
            if (!hasSeenPopup) {
                setInternalOpen(true);
                // Delay visibility for smooth animation
                visibilityTimerRef.current = setTimeout(() => {
                    setIsVisible(true);
                }, 50);

                // Mark as seen after specified time (but don't auto-close unless autoClose is true)
                if (autoMarkSeenAfter) {
                    timerRef.current = setTimeout(() => {
                        localStorage.setItem(`kodeo.${storageKey}`, 'true');
                        // Only auto-close if explicitly requested
                        if (autoClose) {
                            setIsVisible(false);
                            setTimeout(() => setInternalOpen(false), 300); // Wait for fade out
                        }
                    }, autoMarkSeenAfter);
                }
            }
        } else if (open) {
            // If no storageKey, just use the open prop directly
            setInternalOpen(true);
            visibilityTimerRef.current = setTimeout(() => {
                setIsVisible(true);
            }, 50);
        } else {
            setIsVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            if (visibilityTimerRef.current) {
                clearTimeout(visibilityTimerRef.current);
            }
        };
    }, [open, storageKey, autoMarkSeenAfter, autoClose]);

    const handleInteraction = () => {
        if (storageKey) {
            // Clear timer and immediately hide popup
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            localStorage.setItem(`kodeo.${storageKey}`, 'true');
            setIsVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }

        // Don't call onInteraction here - let parent handle their own logic
    };

    const shouldShow = storageKey ? internalOpen : open;

    // Determine positioning and animation direction
    const isBottom = bottom !== null;
    const isRight = right !== null;
    const isLeft = left !== null;
    const isTop = !isBottom && top !== null;

    // Build position styles
    let positionStyles = { width: width };

    // For horizontal tooltips (right/left), we need vertical positioning
    if (isRight || isLeft) {
        // Use top/bottom for vertical centering
        if (top === "50%" || top === 50) {
            positionStyles.top = "50%"; // Vertically centered
        } else if (top) {
            positionStyles.top = top;
        } else if (bottom) {
            positionStyles.top = bottom;
        } else {
            positionStyles.top = "50%"; // Default to center
        }

        // Set horizontal position
        if (isRight) positionStyles.left = right;
        else if (isLeft) positionStyles.right = left;
    } else {
        // For vertical tooltips (top/bottom), standard positioning
        if (isBottom) positionStyles.top = bottom;
        else if (isTop) positionStyles.bottom = top;

        positionStyles.left = '50%'; // Horizontally centered
    }

    // Determine transform origin and slide direction
    let transformOrigin = 'center';
    let slideOffset = { x: 0, y: 0 };

    if (isRight) {
        transformOrigin = 'left center';
        slideOffset = { x: -20, y: 0 };
    } else if (isLeft) {
        transformOrigin = 'right center';
        slideOffset = { x: 20, y: 0 };
    } else if (isBottom) {
        transformOrigin = 'top center';
        slideOffset = { x: 0, y: -20 };
    } else if (isTop) {
        transformOrigin = 'bottom center';
        slideOffset = { x: 0, y: 20 };
    }

    // Arrow positioning classes
    let arrowPositionClass = '';
    let arrowStyles = {};

    if (isRight || isLeft) {
        // Horizontal arrow (pointing left or right)
        arrowPositionClass = isRight ? 'right-full top-1/2 -translate-y-1/2' : 'left-full top-1/2 -translate-y-1/2';
        arrowStyles = isRight ? {
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderRight: '10px solid var(--primary)',
        } : {
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderLeft: '10px solid var(--primary)',
        };
    } else {
        // Vertical arrow (pointing up or down)
        arrowPositionClass = `left-1/2 -translate-x-1/2 ${isBottom ? 'bottom-full' : 'top-full'}`;
        arrowStyles = isBottom ? {
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: '10px solid var(--primary)',
        } : {
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '10px solid var(--primary)',
        };
    }

    // Build dynamic transform for animation
    let baseTransform = '';

    // Add horizontal centering if not positioned left/right
    if (!isRight && !isLeft) {
        baseTransform = 'translateX(-50%)';
    }

    // Add vertical centering for horizontal tooltips (left/right)
    if (isRight || isLeft) {
        baseTransform = 'translateY(-50%)';
    }

    const animatedTransform = isVisible
        ? baseTransform
        : `${baseTransform} translate(${slideOffset.x}px, ${slideOffset.y}px) scale(0.9)`;

    if (!shouldShow) return null;

    return (
        <div
            className={`absolute z-50 p-2 text-base text-center text-white border-2 border-primary-dark bg-primary rounded-xl shadow-lg min-w-36 pointer-events-auto ${storageKey ? 'cursor-pointer' : 'cursor-default'}`}
            style={{
                ...positionStyles,
                transformOrigin,
                transform: animatedTransform,
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                fontFamily: 'inherit',
            }}
            onClick={storageKey ? handleInteraction : undefined}
        >
            <div
                className={`absolute w-0 h-0 ${arrowPositionClass}`}
                style={{
                    ...arrowStyles,
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.3s ease 0.1s'
                }}
            />
            {text}
        </div>
    )
});

export default PopupTooltip